/**
 * Default function used by createComponents to retrieve the container element
 * (parentElement of the component element)
 */
const defaultGetContainerElement = (element: Element) => element.parentElement;

/**
 * Creates components for each element found by the given class name in the document
 * @param className The components class name
 * @param factory The components factory function
 * @param businessLogicOrProperties The businesslogic or properties object
 * @param getContainerElement An optional function which can be used to define,
 * which element is used as container element for the components.
 * If omitted, the parentElement of each element will be used as container.
 */
export const createComponents = <TProps, TComponent>(
    className: string,
    factory: (containerElement: Element, businessLogicOrProperties: TProps) => TComponent,
    businessLogicOrProperties: TProps,
    getContainerElement: (element: Element) => Element = defaultGetContainerElement,
): void => {
    const elements = document.getElementsByClassName(className);

    for (const element of elements) {
        createComponent<TProps, TComponent>(factory, getContainerElement(element), businessLogicOrProperties);
    }
};

/**
 * Receives the component's factory function, the containerElement and businessLogicOrProperties
 * and wraps the factory call in a try-catch so components don't influence each other
 * @param factory The components factory function
 * @param containerElement The container element
 * @param businessLogicOrProperties The businesslogic or properties object
 */
export const createComponent = <TProps, TComponent>(
    factory: (containerElement: Element, businessLogicOrProperties: TProps) => TComponent,
    containerElement: Element,
    businessLogicOrProperties: TProps,
): TComponent | null => {
    try {
        return factory(containerElement, businessLogicOrProperties);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create component', containerElement, error);

        return null;
    }
};
